import React, { useState, lazy, Suspense } from 'react';
import { InformationCircleIcon, ArrowRightCircleIcon } from '@heroicons/react/24/outline';
import trackingService, {
  type TraceResult,
  type TraceCarrierType,
} from '../../services/tracking.service';
import { useToast } from '../../contexts/ToastContext';
import { useTraceResults } from './TraceResultsContext';
// Lazy on purpose. This button is now built into the shared PRO column set, which
// `useAdditionalFields` imports — so EVERY grid offering added order columns
// (Templates, Repair Orders, the Dispatch Planner, …) was statically pulling
// TraceResultModal, and through its embedded ProDetailSlideOver the whole pro
// subtree, into its own module graph. Deferring the modal until a trace actually
// runs keeps the carrier cells cheap for pages that never trace.
const TraceResultModal = lazy(() => import('./TraceResultModal'));

/**
 * Click-trace a single PRO. Routes to gwiTrace or legacy on the backend;
 * shows the result (and gwiTrace snapshot) in a modal. Errors surface as a
 * toast as well as in the modal.
 */
interface Props {
  pronumber: number;
  disabled?: boolean;
  className?: string;
  /** 'button' (default) = labeled blue button; 'icon' = compact ⓘ info icon
   *  (v1 gwitraceinfo style); 'arrow' = the v1 ➡️ run-trace control that sits
   *  next to the Terminal / Trace Container field. */
  variant?: 'button' | 'icon' | 'arrow';
  /** Forces the legacy carrier side (scac=SSLine, rail=Terminal). Omitted =
   *  the backend auto-detects. The per-field ➡️ buttons pass this. */
  tracetype?: TraceCarrierType;
  /** The carrier value currently selected in the field (e.g. the Terminal
   *  dropdown), possibly an unsaved edit. Sent as an override so the trace
   *  honors the on-screen choice instead of the persisted order value. */
  carrier?: string;
  /** Called after a non-error trace with the routed result, so the host can
   *  overlay the freshly-traced values onto the order screen (and highlight
   *  them) in place — no re-fetch, so unsaved edits survive. */
  onTraced?: (result: TraceResult) => void;
  /** Glyph size for the 'arrow' / 'icon' variants (default 'h-5 w-5' / 'h-4 w-4').
   *  The grid cells pass a smaller value so the control fits a table row without
   *  growing it. */
  iconClassName?: string;
  /** Fired the moment the trace is REQUESTED, before any result is known. The grid
   *  cells use it to mark themselves clicked: a trace can legitimately come back
   *  with nothing (it only just opened the carrier connection), so "did I already
   *  run this one" has to be recorded on the click, not on the outcome. */
  onTraceStart?: () => void;
}

const DEFAULT_CLS =
  'px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2';

// These live in tight flex rows next to a fixed-width Terminal/SSLine dropdown.
// shrink-0 keeps the 24px hit-box from collapsing; relative z-10 keeps it above
// an adjacent field that would otherwise overflow-cover it. Without both, the
// clickable area ends up smaller than / left of the visible icon, or gone
// entirely. (The dropdown also gets min-w-0 in OrderFormView so it yields space
// instead of pushing this button out of its half.)
const ICON_CLS =
  'inline-flex shrink-0 relative z-10 items-center justify-center h-6 w-6 rounded text-slate-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 disabled:opacity-50';

const ARROW_CLS =
  'inline-flex shrink-0 relative z-10 items-center justify-center h-6 w-6 rounded text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50';

const TraceButton: React.FC<Props> = ({ pronumber, disabled, className, variant = 'button', tracetype, carrier, onTraced, iconClassName, onTraceStart }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TraceResult | null>(null);
  // Page-level modal owner, when the host mounts one. A button living in a TABLE
  // CELL must use it: the cell is remounted whenever the grid rebuilds its columns
  // or rows — which the trace's own row refresh does — and a modal held in this
  // component's state would be torn down with it, before the user ever saw it.
  // Null on /pro and anywhere else without a provider, which keeps the local modal.
  const hoisted = useTraceResults();

  const show = (r: TraceResult) => {
    if (hoisted) hoisted.show(`Trace — PRO ${pronumber}`, [r]);
    else setResult(r);
  };

  const run = async () => {
    onTraceStart?.();
    setLoading(true);
    // The popup opens when the port has answered, not before. Tried the other
    // way on 2026-10-14 (open at once on the stored snapshot, replace in
    // place): the real answer took 6-8 s on a booking with containers, and a
    // popup that changes under the LC's eyes that long after it opened is
    // worse than the wait. The stored endpoint (`?stored=true`) still exists
    // and is unused here.
    let r: TraceResult;
    try {
      r = await trackingService.trace(pronumber, tracetype, carrier);
    } catch (e) {
      // Tracing WRITES the order, so /tracking/trace is behind require_write_access
      // (Azure `Data.ReadWrite` plus a per-endpoint group grant). Neither is
      // mirrored on the client, so a read-only user can only find out by clicking
      // — say so in their language instead of surfacing "Missing scope:
      // Data.ReadWrite" / "Access denied: No group permission found for POST …".
      const err = e as { response?: { status?: number; data?: { detail?: string } } } | undefined;
      const status = err?.response?.status;
      showToast({
        type: 'error',
        message:
          status === 403
            ? 'You do not have permission to trace orders.'
            : err?.response?.data?.detail || 'Trace failed',
      });
      setLoading(false);
      return;
    }
    // From here the trace HAS run (the server answered 200 and already wrote
    // the order). Showing the result and overlaying it onto the screen are
    // display work: if either throws, log it, but never report the trace as
    // failed — on 2026-10-13 the first click of a registered-on-click export
    // booking returned 200 in 2.4 s and the CSR still saw "Trace failed",
    // because this whole block sat inside the request's try/catch.
    try {
      show(r);
    } catch (e) {
      console.error('Trace result could not be shown', e);
    }
    if (r.status === 'error') {
      showToast({ type: 'error', message: r.summary });
    } else {
      // Hand the result to the host so it can overlay the freshly-traced
      // values onto the order fields in place (no re-fetch = edits survive).
      try {
        onTraced?.(r);
      } catch (e) {
        console.error('Trace overlay failed', e);
      }
    }
    setLoading(false);
  };

  // Stop the click at the button. These controls now sit INSIDE table cells, and
  // the grids that host them bind their own row handlers (open the pro drawer,
  // row-click edit, mark viewed) — without this, running a trace also opened the
  // order, which is the one thing tracing from the grid exists to avoid.
  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    void run();
  };

  const runTitle =
    tracetype === 'rail'
      ? 'Run trace on terminal'
      : tracetype === 'scac'
        ? 'Run trace on SSLine'
        : 'Run trace';

  return (
    <>
      {variant === 'arrow' ? (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled || loading}
          title={runTitle}
          aria-label={runTitle}
          className={className ?? ARROW_CLS}
        >
          <ArrowRightCircleIcon className={`${iconClassName ?? 'h-5 w-5'} ${loading ? 'animate-pulse' : ''}`} />
        </button>
      ) : variant === 'icon' ? (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled || loading}
          title="Show gwiTrace tracking info"
          aria-label="Show gwiTrace tracking info"
          className={className ?? ICON_CLS}
        >
          <InformationCircleIcon className={`${iconClassName ?? 'h-4 w-4'} ${loading ? 'animate-pulse' : ''}`} />
        </button>
      ) : (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled || loading}
          className={className ?? DEFAULT_CLS}
        >
          {loading ? 'Tracing…' : 'Trace'}
        </button>
      )}
      {/* Local modal only when no page-level owner is mounted (see `hoisted`). */}
      {result !== null && (
        // No fallback: the modal is the only thing being awaited and it arrives in a
        // tick — a spinner would flash for less time than it takes to read.
        <Suspense fallback={null}>
          <TraceResultModal
            open
            title={`Trace — PRO ${pronumber}`}
            results={[result]}
            onClose={() => setResult(null)}
          />
        </Suspense>
      )}
    </>
  );
};

export default TraceButton;
